import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Info, StickyNote, CheckSquare, Users, FileText, Star, Activity, TrendingUp, ChevronRight } from "lucide-react";
import { Icons } from "@/components/icons";
import { Button } from "@/components/ui/button";
import NavLinks from "./NavLinks";
import AddLeadLink from "./AddLeadLink";
import CompanySwitcher from "@/components/CompanySwitcher";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { useSidebar } from "@/context/SidebarContext";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const DesktopNavSheet = () => {
  const { profile } = useAuth();
  const navContext = useNavigationContext();
  const navigate = useNavigate();
  const { leadName, onTabChange, activeTab } = useSidebar();
  const location = useLocation();
  
  // Lead-specific sections matching tabs
  const leadSections = [
    { id: 'overview', label: 'Overview', icon: Info, color: 'text-blue-500', gradient: 'from-blue-500/15 to-blue-500/5' },
    { id: 'notes', label: 'Notes', icon: StickyNote, color: 'text-yellow-500', gradient: 'from-yellow-500/15 to-yellow-500/5' },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare, color: 'text-green-500', gradient: 'from-green-500/15 to-green-500/5' },
    { id: 'contacts', label: 'Contacts', icon: Users, color: 'text-purple-500', gradient: 'from-purple-500/15 to-purple-500/5' },
    { id: 'documents', label: 'Documents', icon: FileText, color: 'text-cyan-500', gradient: 'from-cyan-500/15 to-cyan-500/5' },
    { id: 'reviews', label: 'Reviews', icon: Star, color: 'text-orange-500', gradient: 'from-orange-500/15 to-orange-500/5' },
    { id: 'activity', label: 'Activity', icon: Activity, color: 'text-pink-500', gradient: 'from-pink-500/15 to-pink-500/5' },
    { id: 'score', label: 'Score', icon: TrendingUp, color: 'text-emerald-500', gradient: 'from-emerald-500/15 to-emerald-500/5' },
  ];
  
  const getSubscriptionBadge = () => {
    const subscription = JSON.parse(localStorage.getItem('subscription') || '{"tier":"free"}');
    const tier = subscription.tier || 'free';
    switch (tier) {
      case 'starter':
        return 'Starter';
      case 'pro':
        return 'Pro';
      default:
        return 'Free';
    }
  };
  
  return (
    <div className="w-64 h-full bg-gradient-to-b from-background via-background to-muted/20 flex flex-col border-r border-border/60 shadow-sm relative overflow-hidden">
      {/* Subtle dot pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} />
      
      <div className="relative z-10 flex-1 px-4 py-6 overflow-y-auto scrollbar-hide">
        {/* Company Switcher */}
        <div className="mb-6">
          <CompanySwitcher className="w-full" />
        </div>

        {/* Context-Aware Content */}
        {navContext.context === 'lead-details' ? (
          <>
            {/* Back Button */}
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-11 text-sm font-medium hover:bg-gradient-to-r hover:from-muted hover:to-muted/50 hover:border-l-2 hover:border-primary transition-all group"
                onClick={() => navigate('/app')}
              >
                <ArrowLeft className="h-4 w-4 mr-3 group-hover:-translate-x-1 transition-transform" />
                Back to Leads
              </Button>
            </div>
            
            {/* Lead Name */}
            {leadName && (
              <div className="mb-6 pb-4 border-b border-border/60">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Current Lead</p>
                <p className="text-base font-bold truncate bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">{leadName}</p>
              </div>
            )}
            
            {/* Lead Sections */}
            <nav className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">Sections</p>
              {leadSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeTab === section.id || (!activeTab && section.id === 'overview');
                return (
                  <Button
                    key={section.id}
                    variant={isActive ? "secondary" : "ghost"}
                    size="sm"
                    className={cn(
                      "w-full justify-start h-10 text-sm font-medium group relative transition-all duration-200",
                      isActive && [
                        "bg-gradient-to-r",
                        section.gradient,
                        "border-l-2 border-primary",
                        "shadow-sm"
                      ],
                      !isActive && "hover:bg-gradient-to-r hover:from-muted hover:to-muted/50 hover:border-l-2 hover:border-primary/50"
                    )}
                    onClick={() => {
                      // Update the app state so the content changes immediately
                      onTabChange?.(section.id);
                      // Keep URL hash in sync without reload
                      const currentPath = location.pathname;
                      navigate(`${currentPath}#${section.id}`, { replace: true });
                    }}
                  >
                    <Icon className={cn(
                      "h-4 w-4 mr-3 transition-all",
                      isActive ? section.color : "text-muted-foreground",
                      "group-hover:scale-110"
                    )} />
                    {section.label}
                    
                    {isActive && (
                      <span className="absolute right-3 h-2 w-2 rounded-full bg-primary animate-pulse" />
                    )}
                  </Button>
                );
              })}
            </nav>
          </>
        ) : (
          <>
            {/* Quick Actions Section - Moved to Top */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">Quick Actions</p>
              <AddLeadLink className="w-full justify-center h-11 text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 bg-gradient-to-r from-primary via-primary to-primary/90" />
            </div>
            
            <Separator className="mb-6" />
            
            {/* Regular Navigation */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-2">Navigation</p>
              <nav className="space-y-1">
                <NavLinks isMobile={false} />
              </nav>
            </div>
          </>
        )}
      </div>
      
      {/* Sidebar Footer with Subscription Badge */}
      <div className="relative z-10 mt-auto pt-4 px-4 pb-4 border-t border-border/60 bg-gradient-to-t from-muted/30 to-transparent">
        <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 hover:border-primary/40 transition-all cursor-pointer group">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground font-bold text-sm shadow-sm">
              {profile?.first_name?.[0] || 'U'}
            </div>
            <div className="flex flex-col">
              <p className="text-xs font-semibold truncate max-w-[120px]">
                {profile?.first_name || 'User'}
              </p>
              <Badge 
                variant="secondary" 
                className="text-[10px] px-2 py-0 h-5 bg-gradient-to-r from-primary/20 to-primary/10 border-primary/30 font-semibold"
              >
                {getSubscriptionBadge()} Plan
              </Badge>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </div>
  );
};

export default DesktopNavSheet;